// Presentational primitives for the scored Fatigue dashboard.
//
// The prototype's .kpi/.pill/.card/.chip/.fbar/.sec-hdr CSS classes, rebuilt as
// Tailwind components on the app's shadcn tokens (success/warning/destructive/
// info) so the dashboard follows the active brand + light/dark mode instead of
// the prototype's hard-coded dark palette.
//
// Tone mapping from the prototype: teal → success, amber → warning, red →
// destructive, blue → info.

import React from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'violet' | 'muted';

export const toneText: Record<Tone, string> = {
  good: 'text-success',
  warn: 'text-warning',
  bad: 'text-destructive',
  info: 'text-info',
  violet: 'text-violet',
  muted: 'text-muted-foreground',
};
export const toneBg: Record<Tone, string> = {
  good: 'bg-success',
  warn: 'bg-warning',
  bad: 'bg-destructive',
  info: 'bg-info',
  violet: 'bg-violet',
  muted: 'bg-muted-foreground',
};
/** Tinted surface + border, used for pills, alert KPIs and banners. */
export const toneSurface: Record<Tone, string> = {
  good: 'border-success/30 bg-success/10 text-success',
  warn: 'border-warning/30 bg-warning/10 text-warning',
  bad: 'border-destructive/30 bg-destructive/10 text-destructive',
  info: 'border-info/30 bg-info/10 text-info',
  violet: 'border-violet/30 bg-violet/10 text-violet',
  muted: 'border-border bg-muted text-muted-foreground',
};
/** CSS colour for inline styles / recharts (SVG can't use Tailwind classes). */
export const toneVar: Record<Tone, string> = {
  good: 'var(--color-success)',
  warn: 'var(--color-warning)',
  bad: 'var(--color-destructive)',
  info: 'var(--color-info)',
  violet: 'var(--color-violet)',
  muted: 'var(--color-muted-foreground)',
};

/** FED-style 0–100 score → tone (prototype fedColor: ≥80 red, ≥60 amber, else teal). */
export const fedTone = (f: number): Tone => (f >= 80 ? 'bad' : f >= 60 ? 'warn' : 'good');

export const Pill: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = 'muted',
  className,
  children,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] px-1.5 py-0.5 text-[11px] font-medium',
      toneSurface[tone].replace(/border-\S+/, 'border-transparent'),
      className,
    )}
  >
    {children}
  </span>
);

export const Dot: React.FC<{ tone?: Tone; color?: string; className?: string }> = ({ tone, color, className }) => (
  <span
    className={cn('inline-block h-2 w-2 shrink-0 rounded-full', tone && toneBg[tone], className)}
    style={color ? { background: color } : undefined}
  />
);

/** Headline number tile. `tone` tints the whole tile (prototype .kpi.alert/.warn/.good). */
export const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
  /** Colour the sub line: 'up' = worse (destructive), 'dn' = better (success). */
  subTrend?: 'up' | 'dn';
  className?: string;
  onClick?: () => void;
}> = ({ label, value, sub, tone, subTrend, className, onClick }) => (
  <Card
    onClick={onClick}
    className={cn('px-4 py-3.5', onClick && 'cursor-pointer transition-colors hover:bg-accent/50', className)}
  >
    <div className='mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
      {tone && tone !== 'muted' && <span className={cn('h-1.5 w-1.5 rounded-full', toneBg[tone])} />}
      {label}
    </div>
    <div className='text-2xl font-semibold leading-none tracking-tight tabular-nums'>{value}</div>
    {sub != null && (
      <div
        className={cn(
          'mt-2 text-xs text-muted-foreground',
          subTrend === 'up' && 'text-destructive',
          subTrend === 'dn' && 'text-success',
        )}
      >
        {sub}
      </div>
    )}
  </Card>
);

export const KpiRow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('grid grid-cols-[repeat(auto-fit,minmax(168px,1fr))] gap-3', className)}>{children}</div>
);

/** Card with the prototype's header row (title + subtitle + right-side actions). */
export const Panel: React.FC<{
  title?: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, sub, actions, className, children }) => (
  <Card className={cn('p-5', className)}>
    {(title || actions) && (
      <div className='mb-4 flex items-center justify-between gap-3'>
        <div className='min-w-0'>
          {title && <div className='text-sm font-semibold tracking-tight'>{title}</div>}
          {sub && <div className='mt-0.5 text-xs text-muted-foreground'>{sub}</div>}
        </div>
        {actions && <div className='flex shrink-0 items-center gap-2'>{actions}</div>}
      </div>
    )}
    {children}
  </Card>
);

export const SecHeader: React.FC<{ children: React.ReactNode; actions?: React.ReactNode; className?: string }> = ({
  children,
  actions,
  className,
}) => (
  <div className={cn('mb-3 flex items-center gap-2 text-xs font-medium text-muted-foreground', className)}>
    {children}
    {actions && <div className='ml-auto flex gap-2'>{actions}</div>}
  </div>
);

/** Responsive grids from the prototype (.grid-2/.grid-3/.grid-1-2). */
export const Grid: React.FC<{ cols: 2 | 3 | '1-2'; className?: string; children: React.ReactNode }> = ({
  cols,
  className,
  children,
}) => (
  <div
    className={cn(
      'grid grid-cols-1 gap-4',
      cols === 2 && 'lg:grid-cols-2',
      cols === 3 && 'lg:grid-cols-3',
      cols === '1-2' && 'lg:grid-cols-[1fr_2fr]',
      className,
    )}
  >
    {children}
  </div>
);

/** Selectable chip (range / filter pickers). */
export const Chip: React.FC<{ active?: boolean; onClick?: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type='button'
    onClick={onClick}
    className={cn(
      'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
      active
        ? 'border-foreground bg-foreground text-background'
        : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
    )}
  >
    {children}
  </button>
);

/** Segmented tab group (prototype .drill-nav / .drill-btn). */
export function Tabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: React.ReactNode }[];
}) {
  return (
    <div className='inline-flex flex-wrap items-center gap-0.5 rounded-md bg-muted p-0.5'>
      {options.map((o) => (
        <button
          key={o.key}
          type='button'
          onClick={() => onChange(o.key)}
          className={cn(
            'flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-[13px] font-medium transition-colors',
            value === o.key
              ? 'bg-card text-foreground shadow-[0_0_0_1px_var(--color-border)]'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Thin horizontal score bar (prototype .fbar). */
export const FBar: React.FC<{ value: number; max?: number; tone?: Tone; color?: string; className?: string }> = ({
  value,
  max = 100,
  tone = 'good',
  color,
  className,
}) => (
  <div className={cn('h-1 w-full overflow-hidden rounded-[2px] bg-muted', className)}>
    <div
      className={cn('h-full rounded-[2px]', !color && toneBg[tone])}
      style={{ width: `${Math.max(0, Math.min(100, (value / max) * 100))}%`, background: color }}
    />
  </div>
);

export const Banner: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = 'bad',
  className,
  children,
}) => (
  <div
    className={cn(
      'flex items-center gap-2 rounded-md border border-l-2 border-border bg-card px-3.5 py-2.5 text-[13px]',
      tone === 'bad' && 'border-l-destructive',
      tone === 'warn' && 'border-l-warning',
      tone === 'good' && 'border-l-success',
      tone === 'info' && 'border-l-info',
      tone === 'violet' && 'border-l-violet',
      className,
    )}
  >
    {children}
  </div>
);

/** Small score chip used in the FED / CDI / LRM trio. */
export const ScoreChip: React.FC<{ label: string; value: React.ReactNode; sub?: string; color?: string }> = ({
  label,
  value,
  sub,
  color,
}) => (
  <div className='flex min-w-[44px] flex-col items-start gap-0.5 border-l border-border pl-2.5'>
    <span className='text-[10px] font-medium uppercase tracking-wider text-muted-foreground'>{label}</span>
    <span className='text-lg font-semibold leading-none tabular-nums' style={{ color }}>
      {value}
    </span>
    {sub && <span className='text-[10px] leading-none text-muted-foreground'>{sub}</span>}
  </div>
);

/** Plain table styling (quiet headers, hairline rows). */
export const dataTable = {
  wrap: 'overflow-x-auto',
  table: 'w-full border-collapse text-xs',
  th: 'whitespace-nowrap border-b border-border px-2.5 py-1.5 text-left text-[11px] font-medium text-muted-foreground',
  td: 'border-b border-border px-2.5 py-2 align-middle group-last:border-b-0',
  num: 'tabular-nums text-right',
  tr: 'group transition-colors hover:bg-muted/50',
} as const;
